import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { CartTierChangeEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * In "tier mode" (data-tier-mode="true", set when the line's product has a pcs-tier
 * "Quantity" option — see snippets/quantity-selector.liquid) the +/- buttons don't do
 * quantity math at all: they request a swap to the next/previous tier variant instead.
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * @returns {boolean} Whether this selector is switching pcs-tier variants instead of counting packs
   */
  get isTierMode() {
    return this.dataset.tierMode === 'true';
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;

    if (this.isTierMode) {
      minusButton.disabled = !minusButton.dataset.prevVariantId;
      plusButton.disabled = !plusButton.dataset.nextVariantId;
      return;
    }

    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (this.isTierMode) {
      if (!(event.target instanceof HTMLElement)) return;
      event.preventDefault();
      this.#requestTierChange(this.refs.plusButton.dataset.nextVariantId);
      return;
    }
    super.increaseQuantity(event);
  }

  /**
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (this.isTierMode) {
      if (!(event.target instanceof HTMLElement)) return;
      event.preventDefault();
      this.#requestTierChange(this.refs.minusButton.dataset.prevVariantId);
      return;
    }
    super.decreaseQuantity(event);
  }

  /**
   * @param {Event} event
   */
  setQuantity(event) {
    if (this.isTierMode) return;
    super.setQuantity(event);
  }

  /**
   * @param {string | undefined} variantId
   */
  #requestTierChange(variantId) {
    if (!variantId) return;

    const cartLine = Number(this.refs.quantityInput.dataset.cartLine) || undefined;
    this.dispatchEvent(new CartTierChangeEvent(Number(variantId), cartLine));
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
